import { useCallback, useEffect, useState } from 'react';

const DISMISS_KEY = 'appInstallHinweisGeschlossen';

// `beforeinstallprompt` feuert nur einmal kurz nach dem Laden der Seite – in
// der Regel bevor der Nutzer eingeloggt auf dem Dashboard ankommt. Das Event
// wird deshalb schon beim Laden dieses Moduls abgefangen (es hängt über
// App.js → DashboardPage statisch im Bundle) und hier zwischengespeichert.
let installPrompt = null;
const listeners = new Set();
const notify = () => listeners.forEach((listener) => listener());

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault();
    installPrompt = event;
    notify();
  });
  window.addEventListener('appinstalled', () => {
    installPrompt = null;
    notify();
  });
}

/** Läuft die Seite bereits als installierte App (Android/Desktop bzw. iOS-Homescreen)? */
function laeuftAlsApp() {
  return Boolean(window.matchMedia?.('(display-mode: standalone)').matches)
    || window.navigator.standalone === true;
}

/** iPhone/iPad – iPadOS meldet sich als „MacIntel", erkennbar am Touch-Display. */
function istIos() {
  const { userAgent, platform, maxTouchPoints } = window.navigator;
  return /iPad|iPhone|iPod/.test(userAgent) || (platform === 'MacIntel' && maxTouchPoints > 1);
}

function leseGeschlossen() {
  try {
    return localStorage.getItem(DISMISS_KEY) === 'true';
  } catch {
    // localStorage nicht verfügbar (privater Modus o. Ä.) – Hinweis bleibt dann sichtbar.
    return false;
  }
}

/**
 * Stellt fest, ob und wie dem Nutzer die Installation als App angeboten wird.
 *
 * `modus`:
 *  - 'prompt': Browser bietet die Installation an (Chrome/Edge/Android) – `installieren()` öffnet den Dialog.
 *  - 'ios': Safari auf iPhone/iPad kennt keinen Dialog, die Installation läuft manuell über „Teilen".
 *  - null: nichts anzeigen (bereits installiert, Hinweis geschlossen oder Browser ohne Installationsmöglichkeit).
 *
 * Mit `dauerhaft` wird ein zuvor geschlossener Hinweis trotzdem angeboten (z. B. im Profil,
 * wo die Installation jederzeit wiederzufinden sein soll).
 */
export default function useAppInstallation({ dauerhaft = false } = {}) {
  const [, setVersion] = useState(0);
  const [geschlossen, setGeschlossen] = useState(leseGeschlossen);

  useEffect(() => {
    const listener = () => setVersion((v) => v + 1);
    listeners.add(listener);
    return () => { listeners.delete(listener); };
  }, []);

  const schliessen = useCallback(() => {
    try {
      localStorage.setItem(DISMISS_KEY, 'true');
    } catch {
      // Nur für diese Sitzung ausblenden.
    }
    setGeschlossen(true);
  }, []);

  const installieren = useCallback(async () => {
    const prompt = installPrompt;
    if (!prompt) return;
    prompt.prompt();
    const { outcome } = await prompt.userChoice;
    // Das Event ist nur einmal verwendbar.
    installPrompt = null;
    notify();
    // Wer den Dialog bewusst ablehnt, soll nicht erneut gefragt werden.
    if (outcome === 'dismissed') schliessen();
  }, [schliessen]);

  let modus = null;
  if ((dauerhaft || !geschlossen) && !laeuftAlsApp()) {
    if (installPrompt) modus = 'prompt';
    else if (istIos()) modus = 'ios';
  }

  return { modus, installieren, schliessen };
}
